/**
 * Frequency Configuration Utilities
 * Extracts and manages frequency configurations from getMasterDataFilters API response
 */

/**
 * Normalizes frequency string to match DateFilter format
 * @param {string} frequency - API frequency value (e.g., "weekly", "monthly", "yearly")
 * @returns {string} - Normalized frequency (e.g., "Weekly", "Monthly", "Yearly")
 */
export const normalizeFrequency = (frequency) => {
  if (!frequency) {
    return null;
  }
  return frequency.charAt(0).toUpperCase() + frequency.slice(1).toLowerCase();
};

/**
 * Extracts frequency configurations for a specific tab or subTab
 * @param {Object} params - Parameters object
 * @param {Object} params.filtersData - The getMasterDataFilters response data
 * @param {number} params.programId - Selected program ID
 * @param {number} params.productId - Selected product ID
 * @param {number|null} params.modelId - Selected model ID (can be null)
 * @param {string} params.tabName - Selected tab name
 * @param {string|null} params.subTabName - Selected subTab name (optional)
 * @returns {Array} - Array of frequency configurations
 */
export const getFrequencyConfig = ({
  filtersData,
  programId,
  productId,
  modelId,
  tabName,
  subTabName = null,
}) => {
  try {
    // Validate input
    if (!filtersData?.programs || !programId || !productId || !tabName) {
      return [];
    }

    // Find program
    const program = filtersData.programs.find((p) => p.programId === programId);
    if (!program) {
      return [];
    }

    // Find product
    const product = program.products.find((p) => p.productId === productId);
    if (!product) {
      return [];
    }

    // Find model
    const model = product.models.find((m) => m.modelId === modelId);
    if (!model || !model.tabs) {
      return [];
    }

    // Find tab
    const tab = model.tabs.find(
      (t) => t.tabName.toLowerCase() === tabName.toLowerCase()
    );
    if (!tab) {
      return [];
    }

    // If subTabName is provided, look for subTab frequencies
    if (subTabName && tab.subTabs) {
      const subTab = tab.subTabs.find(
        (st) => st.subTabName.toLowerCase() === subTabName.toLowerCase()
      );
      if (subTab && subTab.frequencies) {
        return subTab.frequencies.map((freq) => ({
          ...freq,
          frequency: normalizeFrequency(freq.frequency),
        }));
      }
    }

    // Return tab-level frequencies
    if (tab.frequencies) {
      return tab.frequencies.map((freq) => ({
        ...freq,
        frequency: normalizeFrequency(freq.frequency),
      }));
    }

    return [];
  } catch (error) {
    console.error('Error extracting frequency config:', error);
    return [];
  }
};

/**
 * Converts frequency configurations to DateFilter option format
 * @param {Array} frequencyConfigs - Array of frequency configurations
 * @returns {Array} - Array of options for DateFilter
 *
 * Default periodDays values:
 * - Weekly: 28 days (4 weeks)
 * - Monthly: 182 days (approximately 6 months)
 * - Yearly: 365 days (1 year)
 */
export const convertToFrequencyOptions = (frequencyConfigs) => {
  if (!Array.isArray(frequencyConfigs) || frequencyConfigs.length === 0) {
    // Default fallback to all frequencies with default periodDays if none configured
    return [
      {
        id: 'Weekly',
        label: 'Weekly',
        config: {
          startDay: 1, // Monday
          endDay: 0, // Sunday
          periodDays: 28,
        },
      },
      {
        id: 'Monthly',
        label: 'Monthly',
        config: {
          startDay: null,
          endDay: null,
          periodDays: 182,
        },
      },
      {
        id: 'Yearly',
        label: 'Yearly',
        config: {
          startDay: null,
          endDay: null,
          periodDays: 365,
        },
      },
    ];
  }

  return frequencyConfigs.map((config) => ({
    id: config.frequency,
    label: config.frequency,
    config: {
      startDay: config.startDay,
      endDay: config.endDay,
      periodDays: config.periodDays,
    },
  }));
};

/**
 * Gets the week configuration (start/end days) for weekly frequency
 * When a marketId is provided, checks the weekly config's marketOverrides array
 * for a matching entry and uses its startDay/endDay if found.
 * @param {Array} frequencyConfigs - Available frequency configurations
 * @param {number|null} marketId - Selected market ID (optional, used for market-specific overrides)
 * @returns {Object|null} - Week config { startDay, endDay } or null
 */
export const getWeekConfig = (frequencyConfigs, marketId = null) => {
  if (!Array.isArray(frequencyConfigs)) {
    return null;
  }

  const weeklyConfig = frequencyConfigs.find(
    (config) => config.frequency.toLowerCase() === 'weekly'
  );

  if (!weeklyConfig) {
    return null;
  }

  // Check for market-specific override
  if (marketId && Array.isArray(weeklyConfig.marketOverrides)) {
    const override = weeklyConfig.marketOverrides.find(
      (mo) => mo.marketId === marketId
    );
    if (override) {
      return {
        startDay: override.startDay,
        endDay: override.endDay,
      };
    }
  }

  return {
    startDay: weeklyConfig.startDay,
    endDay: weeklyConfig.endDay,
  };
};

/**
 * Validates if a frequency is available for current selection
 * @param {Array} frequencyConfigs - Available frequency configurations
 * @param {string} frequency - Frequency to validate (e.g., 'Weekly')
 * @returns {boolean} - True if frequency is available
 */
export const isFrequencyAvailable = (frequencyConfigs, frequency) => {
  if (!Array.isArray(frequencyConfigs) || frequencyConfigs.length === 0) {
    return true; // If no config, all frequencies available
  }
  return frequencyConfigs.some(
    (config) => config.frequency.toLowerCase() === frequency.toLowerCase()
  );
};

/**
 * Selects frequency based on current selection and available options
 * Preserves current selection if available in new tab, otherwise defaults to Monthly
 * @param {Array} frequencyOptions - Available frequency options [{id, label, config}]
 * @param {Object} currentFrequency - Currently selected frequency {id, label, config}
 * @returns {Object|null} - Selected frequency option or null
 */
export const selectFrequencyWithPreference = (
  frequencyOptions,
  currentFrequency
) => {
  if (!Array.isArray(frequencyOptions) || frequencyOptions.length === 0) {
    return null;
  }

  // Priority 1: Keep current selection if available in new options
  if (currentFrequency?.id) {
    const currentOption = frequencyOptions.find(
      (option) => option.id === currentFrequency.id
    );
    if (currentOption) {
      return currentOption;
    }
  }

  // Priority 2: Default to 'Monthly' if not available
  const monthlyOption = frequencyOptions.find(
    (option) => option.id === 'Monthly'
  );
  if (monthlyOption) {
    return monthlyOption;
  }

  // Priority 3: Fallback to first available option (edge case)
  return frequencyOptions[0];
};
